// OOO-DELEGATE: "Out of office" on the account page. A person sets dates and a delegate (another
// person of the company, never a digital employee); while away, approvals, escalations and DE
// questions routed to them go to the delegate, marked "covering for <name>", or to the next person
// up the org chart when no delegate is set. Ending early restores routing. On return the page
// lists what was routed away. The section is absent while the feature is off (the API answers 404).
import { Button, Field, FieldLabel, Input, Notice, SettingsGroup } from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import type { Api } from "../../api/client";
import { formatDateTime } from "../format";
import { PagedPicker } from "../paged-picker";
import { peopleQuery } from "../queries";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { problemCode } from "../structure/parts";

const oooKey = (co: string) => ["company", co, "out-of-office"] as const;
const NEXT_UP = "";

/** A local date (yyyy-mm-dd) as the start (00:00) or end (23:59:59) of that day in ISO. */
export const dayBound = (date: string, end: boolean): string =>
  new Date(`${date}T${end ? "23:59:59" : "00:00:00"}`).toISOString();

export function OutOfOffice({
  api,
  co,
  me,
  id = "out-of-office",
  title = "Out of office",
}: {
  api: Api;
  co: string;
  /** The caller's own membership id in this company (not offered as a delegate). */
  me: string;
  id?: string;
  title?: string;
}) {
  const qc = useQueryClient();
  const windows = useQuery({
    queryKey: oooKey(co),
    queryFn: () => api.client.ooo.list({ params: { co } }),
    retry: false,
  });
  const summary = useQuery({
    queryKey: [...oooKey(co), "return"],
    queryFn: () => api.client.ooo.returnSummary({ params: { co } }),
    retry: false,
  });
  const people = useInfiniteQuery(peopleQuery(api, co));
  const delegates = useMemo(
    () =>
      (people.data?.pages.flatMap((p) => p.data) ?? [])
        .filter((p) => p.kind === "person" && p.status === "active" && p.id !== me)
        .map((p) => ({ id: p.id, label: p.display_name })),
    [people.data, me],
  );
  const nameOf = (m: string | null) =>
    m ? (delegates.find((d) => d.id === m)?.label ?? "a colleague") : null;
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [delegate, setDelegate] = useState(NEXT_UP);
  const refresh = () => void qc.invalidateQueries({ queryKey: oooKey(co) });
  const set = useMutation({
    mutationFn: () =>
      api.client.ooo.set({
        params: { co },
        body: {
          starts_at: dayBound(from, false),
          ends_at: dayBound(to, true),
          ...(delegate ? { delegate_membership_id: delegate } : {}),
        },
      }),
    onSuccess: () => {
      setFrom("");
      setTo("");
      setDelegate(NEXT_UP);
      refresh();
    },
  });
  const end = useMutation({
    mutationFn: (windowId: string) => api.client.ooo.end({ params: { co, id: windowId } }),
    onSuccess: refresh,
  });
  const dismiss = useMutation({
    mutationFn: (windowId: string) =>
      api.client.ooo.dismissSummary({ params: { co, id: windowId } }),
    onSuccess: refresh,
  });

  // Off (404) or not a person's own session: nothing to show.
  const code = problemCode(windows.error);
  if (code === "not_found" || code === "forbidden") return null;
  const invalid = !from || !to || dayBound(to, true) <= dayBound(from, false);

  return (
    <SettingsGroup id={id} title={title} data-slot="out-of-office">
      <div className="flex flex-col gap-3 p-4 text-[13px]">
        <p className="text-muted-foreground">
          While you are away, approvals, escalations and questions from digital employees that would
          reach you go to your delegate, marked "covering for you". With no delegate they go to the
          next person up the org chart. End it early and they come back to you at once.
        </p>
        {(summary.data?.data ?? []).map((s) => (
          <Notice key={s.window_id} data-slot="ooo-summary">
            <div className="flex flex-wrap items-center gap-2">
              <span>
                {s.routed.length === 0
                  ? "Welcome back. Nothing was routed away while you were out."
                  : `Welcome back. ${s.routed.length} item${s.routed.length === 1 ? " was" : "s were"} handled while you were out.`}
              </span>
              <Button
                size="sm"
                variant="ghost"
                disabled={dismiss.isPending}
                onClick={() => dismiss.mutate(s.window_id)}
              >
                Dismiss
              </Button>
            </div>
            {s.routed.length > 0 ? (
              <ul className="mt-1 list-disc pl-5">
                {s.routed.map((r) => (
                  <li key={`${r.subject_kind}-${r.subject_id}`}>
                    {r.subject_kind} routed to{" "}
                    {r.via === "delegate" ? "your delegate" : "the next up"} on{" "}
                    {formatDateTime(r.at)}
                  </li>
                ))}
              </ul>
            ) : null}
          </Notice>
        ))}
        {windows.isPending ? (
          <Loading label="Loading out of office" rows={1} />
        ) : windows.isError ? (
          <LoadError what="Out of office" error={windows.error} />
        ) : (
          <ul className="flex flex-col gap-2" data-slot="ooo-windows">
            {windows.data.data.length === 0 ? (
              <li className="text-muted-foreground">You are not set as away.</li>
            ) : (
              windows.data.data.map((w) => (
                <li key={w.id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span>
                    {formatDateTime(w.starts_at)} to {formatDateTime(w.ends_at)}
                    {" · "}
                    {w.delegate_membership_id
                      ? `delegate: ${nameOf(w.delegate_membership_id)}`
                      : "next person up the org chart"}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={end.isPending}
                    onClick={() => end.mutate(w.id)}
                  >
                    {new Date(w.starts_at) <= new Date() ? "End now" : "Cancel"}
                  </Button>
                </li>
              ))
            )}
          </ul>
        )}
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!invalid) set.mutate();
          }}
        >
          <div className="flex flex-wrap gap-3">
            <Field>
              <FieldLabel htmlFor={`${id}-from`}>Away from</FieldLabel>
              <Input
                id={`${id}-from`}
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={`${id}-to`}>Back after</FieldLabel>
              <Input
                id={`${id}-to`}
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </Field>
          </div>
          <PagedPicker
            id={`${id}-delegate`}
            label="Delegate"
            value={delegate}
            onChange={setDelegate}
            options={[{ id: NEXT_UP, label: "No delegate (next person up)" }, ...delegates]}
            hasMore={!!people.hasNextPage}
            loading={people.isFetchingNextPage}
            onMore={() => void people.fetchNextPage()}
          />
          {set.isError ? (
            <p role="alert" className="text-destructive">
              {problemText(set.error, "Could not set out of office")}
            </p>
          ) : null}
          <div>
            <Button type="submit" disabled={invalid || set.isPending}>
              Set out of office
            </Button>
          </div>
        </form>
      </div>
    </SettingsGroup>
  );
}
